import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import About from './About';
import Components from './Components';
import Service from './Service';
import Products from './Products';
import Contact from './Contact';
import BackgroundEffect from './BackgroundEffect';

/* ─── Slide data ─── */
const slides = [
    {
        lineOne: 'Crafting the',
        lineTwo: 'Iconic Future',
        sub: 'Redefining excellence through bold design and precision craftsmanship.',
        btn: 'VIEW WORKS',
        scrollTarget: 'components',
        accent: '#C5A059',
    },
    {
        lineOne: 'Precision in',
        lineTwo: 'Execution',
        sub: 'Every detail engineered to perfection — delivering beyond expectations.',
        btn: 'OUR SERVICES',
        scrollTarget: 'service',
        accent: '#C5A059',
    },
    {
        lineOne: 'Building the',
        lineTwo: 'Extraordinary',
        sub: 'Where vision meets mastery — structures that stand the test of time.',
        btn: 'PORTFOLIO',
        scrollTarget: 'components',
        accent: '#C5A059',
    },
];

const SLIDE_DURATION = 5000;

/* ─── Specialties data ─── */
const specialties = [
    { icon: '⬡', title: 'Interior Design', desc: 'Bespoke spaces with soul' },
    { icon: '◈', title: 'Architecture', desc: 'Structures built to endure' },
    { icon: '◻', title: 'Fit-Out Works', desc: 'Precision at every layer' },
    { icon: '◇', title: 'Renovation', desc: 'Transforming what exists' },
];

/* ─── Smooth scroll helper ─── */
const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
        const top = el.getBoundingClientRect().top + window.pageYOffset - 90;
        window.scrollTo({ top, behavior: 'smooth' });
    }
};

/* ─── Window width hook ─── */
const useWindowWidth = () => {
    const [width, setWidth] = useState(window.innerWidth);
    useEffect(() => {
        const handler = () => setWidth(window.innerWidth);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);
    return width;
};

/* ═══════════════════════════════════════════════════════ */
const Home = () => {
    const location = useLocation();
    const [current, setCurrent] = useState(0);
    const [animState, setAnimState] = useState('in');
    const timerRef = useRef(null);
    const width = useWindowWidth();

    const isMobile = width < 576;
    const isTablet = width >= 576 && width < 992;

    /* Scroll-to-section on route state */
    useEffect(() => {
        if (location.state?.scrollTo) {
            const id = location.state.scrollTo;
            const el = document.getElementById(id);
            if (el) {
                const top = el.getBoundingClientRect().top + window.pageYOffset - 80;
                window.scrollTo({ top, behavior: 'smooth' });
            } else if (id === 'home') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
            window.history.replaceState({}, document.title);
        }
    }, [location]);

    /* Auto-advance */
    useEffect(() => {
        timerRef.current = setInterval(advanceSlide, SLIDE_DURATION);
        return () => clearInterval(timerRef.current);
        // eslint-disable-next-line
    }, [current]);

    const advanceSlide = () => {
        setAnimState('out');
        setTimeout(() => {
            setCurrent(prev => (prev + 1) % slides.length);
            setAnimState('in');
        }, 700);
    };

    const goTo = (i) => {
        if (i === current) return;
        clearInterval(timerRef.current);
        setAnimState('out');
        setTimeout(() => { setCurrent(i); setAnimState('in'); }, 700);
    };

    const slide = slides[current];
    const inAnim = (base, delay) => `${base} 0.75s cubic-bezier(0.19,1,0.22,1) ${delay} both`;
    const outAnim = (base, delay = '0s') => `${base} 0.5s cubic-bezier(0.19,1,0.22,1) ${delay} both`;

    return (
        <main>
            <BackgroundEffect />

            {/* ── Hero ── */}
            <section id="home" className="hero-section">

                {/* Vignette depth overlay */}
                <div className="hero-vignette" />

                {/* Decorative horizontal rule lines */}
                <div className="deco-line deco-line-top" />
                <div className="deco-line deco-line-bottom" />

                {/* Vertical side lines — hidden on mobile */}
                {!isMobile && <div className="vert-line vert-line-left" />}
                {!isMobile && <div className="vert-line vert-line-right" />}

                {/* Corner brackets — hidden on mobile */}
                {!isMobile && <>
                    <div className="corner-bracket" style={{ top: 30, left: 30, borderTop: `1px solid ${slide.accent}`, borderLeft: `1px solid ${slide.accent}` }} />
                    <div className="corner-bracket" style={{ top: 30, right: 30, borderTop: `1px solid ${slide.accent}`, borderRight: `1px solid ${slide.accent}` }} />
                    <div className="corner-bracket" style={{ bottom: 80, left: 30, borderBottom: `1px solid ${slide.accent}`, borderLeft: `1px solid ${slide.accent}` }} />
                    <div className="corner-bracket" style={{ bottom: 80, right: 30, borderBottom: `1px solid ${slide.accent}`, borderRight: `1px solid ${slide.accent}` }} />
                </>}

                {/* Slide counter — tablet+ only */}
                {!isMobile && (
                    <div className="slide-counter">
                        <span
                            key={`cnt-${current}`}
                            className="counter-current"
                            style={{ animation: 'heroFadeUp 0.4s cubic-bezier(0.19,1,0.22,1) both' }}
                        >
                            {String(current + 1).padStart(2, '0')}
                        </span>
                        <div className="counter-line" />
                        <span className="counter-total">
                            {String(slides.length).padStart(2, '0')}
                        </span>
                    </div>
                )}

                {/* Left social sidebar — desktop only */}
                {!isMobile && !isTablet && (
                    <div className="hero-sidebar-left">
                        <a href="#contact" className="sidebar-link"
                            onClick={e => { e.preventDefault(); scrollToSection('contact'); }}>
                            CONTACT
                        </a>
                        <div className="sidebar-divider" />
                        <a href="#about" className="sidebar-link"
                            onClick={e => { e.preventDefault(); scrollToSection('about'); }}>
                            ABOUT US
                        </a>
                        <div className="sidebar-divider" />
                        <a href="#service" className="sidebar-link"
                            onClick={e => { e.preventDefault(); scrollToSection('service'); }}>
                            SERVICES
                        </a>
                    </div>
                )}

                {/* ── Main content ── */}
                <div className="hero-content">


                    {/* Headings */}
                    <div className="hero-heading-wrap">
                        <h1
                            key={`h1-${current}`}
                            className="hero-h1 hero-h1-light"
                            style={{
                                animation: animState === 'in'
                                    ? inAnim('heroSlideUp', '0.2s')
                                    : outAnim('heroFadeDown'),
                            }}
                        >
                            {slide.lineOne}
                        </h1>
                        <h1
                            key={`h2-${current}`}
                            className="hero-h1 hero-h1-gold"
                            style={{
                                animation: animState === 'in'
                                    ? inAnim('heroSlideUp', '0.35s')
                                    : outAnim('heroFadeDown', '0.05s'),
                            }}
                        >
                            {slide.lineTwo}
                            <span className="heading-underline" />
                        </h1>
                    </div>

                    {/* Sub-text */}
                    <p
                        key={`sub-${current}`}
                        className="hero-sub"
                        style={{
                            animation: animState === 'in'
                                ? inAnim('heroFadeUp', '0.5s')
                                : outAnim('heroFadeDown'),
                        }}
                    >
                        {slide.sub}
                    </p>

                    {/* CTA */}
                    <div
                        key={`btn-${current}`}
                        style={{
                            animation: animState === 'in'
                                ? inAnim('heroFadeUp', '0.65s')
                                : outAnim('heroFadeDown'),
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0,
                        }}
                    >
                        <button
                            className="btn-gold"
                            onClick={() => scrollToSection(slide.scrollTarget)}
                        >
                            {slide.btn}
                        </button>
                    </div>

                    {/* ── What We Do strip ── */}
                    <div className="specialties-strip">
                        {specialties.map((sp, i) => (
                            <div
                                key={`sp-${i}`}
                                className="specialty-item"
                                style={{
                                    animation: `heroFadeUp 0.7s cubic-bezier(0.19,1,0.22,1) ${0.75 + i * 0.12}s both`,
                                }}
                                onClick={() => scrollToSection('service')}
                            >
                                <span className="specialty-icon">{sp.icon}</span>
                                <div className="specialty-text">
                                    <span className="specialty-title">{sp.title}</span>
                                    <span className="specialty-desc">{sp.desc}</span>
                                </div>
                                <div className="specialty-arrow">→</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Ghost bg word — tablet+ only */}
                {!isMobile && (
                    <div
                        key={`bg-${current}`}
                        className="bg-ghost-word"
                        style={{
                            animation: animState === 'in'
                                ? inAnim('bgWordIn', '0.1s')
                                : outAnim('bgWordOut'),
                        }}
                    >
                        {slide.lineTwo}
                    </div>
                )}

                {/* Dot indicators */}
                <div className="hero-indicators">
                    {slides.map((_, i) => (
                        <button
                            key={i}
                            className="hero-dot"
                            onClick={() => goTo(i)}
                            style={{
                                width: i === current ? 36 : 8,
                                background: i === current
                                    ? slide.accent
                                    : 'rgba(255,255,255,0.25)',
                            }}
                            aria-label={`Slide ${i + 1}`}
                        />
                    ))}
                </div>

                {/* Progress bar */}
                <div className="hero-progress-bar">
                    <div
                        key={`prog-${current}`}
                        className="hero-progress-fill"
                        style={{ animation: `heroProgress ${SLIDE_DURATION}ms linear both` }}
                    />
                </div>

                {/* Scroll indicator — desktop only */}
                {!isMobile && !isTablet && (
                    <div className="scroll-indicator">
                        <span className="scroll-label">SCROLL</span>
                        <div className="scroll-line" />
                    </div>
                )}
            </section>

            {/* Marquee ribbon */}
            <div className="marquee-wrap" aria-hidden="true">
                <div className="marquee-track">
                    {[...Array(4)].map((_, i) => (
                        <span key={i} className="marquee-text">
                            AL WASEEQ &nbsp;·&nbsp; INTERIOR DESIGN &nbsp;·&nbsp; ARCHITECTURE &nbsp;·&nbsp; EXCELLENCE &nbsp;·&nbsp;
                        </span>
                    ))}
                </div>
            </div>

            <About />
            <Components />
            <Service />
            <Products />
            <Contact />

            {/* ── All styles + keyframes ── */}
            <style>{`
                /* === Hero Section === */
                .hero-section {
                    position: relative;
                    height: 100vh;
                    min-height: 560px;
                    width: 100%;
                    background: radial-gradient(ellipse at 60% 40%, #1a1508 0%, #0A0A0A 60%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    overflow: hidden;
                }

                /* Vignette depth overlay */
                .hero-vignette {
                    position: absolute;
                    inset: 0;
                    background:
                        radial-gradient(ellipse 70% 60% at 50% 50%, transparent 40%, rgba(0,0,0,0.55) 100%),
                        linear-gradient(180deg, rgba(0,0,0,0.35) 0%, transparent 25%, transparent 75%, rgba(0,0,0,0.5) 100%);
                    pointer-events: none;
                    z-index: 2;
                }

                /* Left sidebar quick-nav — desktop only */
                .hero-sidebar-left {
                    position: absolute;
                    left: clamp(14px, 2.5%, 28px);
                    top: 0;
                    bottom: 0;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 14px;
                    z-index: 10;
                    animation: sidebarFadeIn 1s ease 1s both;
                }
                .sidebar-link {
                    font-family: 'Outfit', sans-serif;
                    font-size: 0.55rem;
                    color: rgba(197,160,89,0.45);
                    letter-spacing: 3px;
                    font-weight: 500;
                    text-decoration: none;
                    text-transform: uppercase;
                    writing-mode: vertical-rl;
                    text-orientation: mixed;
                    transform: rotate(180deg);
                    transition: color 0.3s ease;
                    cursor: pointer;
                }
                .sidebar-link:hover { color: #C5A059; }
                .sidebar-divider {
                    width: 1px; height: 28px;
                    background: rgba(197,160,89,0.2);
                }

                /* Specialties strip */
                .specialties-strip {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    gap: 0;
                    margin-top: clamp(24px, 4vw, 40px);
                    padding-top: clamp(16px, 2.5vw, 22px);
                    border-top: 1px solid rgba(197,160,89,0.12);
                    width: 100%;
                    max-width: min(680px, 92%);
                }
                .specialty-item {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 8px;
                    padding: 14px 10px;
                    cursor: pointer;
                    position: relative;
                    border-left: 1px solid rgba(197,160,89,0.08);
                    transition: all 0.35s cubic-bezier(0.19,1,0.22,1);
                    overflow: hidden;
                }
                .specialty-item:first-child { border-left: none; }
                .specialty-item::before {
                    content: '';
                    position: absolute;
                    bottom: 0; left: 0;
                    width: 100%; height: 2px;
                    background: linear-gradient(90deg, #C5A059, transparent);
                    transform: scaleX(0);
                    transform-origin: left;
                    transition: transform 0.4s cubic-bezier(0.19,1,0.22,1);
                }
                .specialty-item:hover::before { transform: scaleX(1); }
                .specialty-item:hover .specialty-icon { color: #C5A059; transform: scale(1.15); }
                .specialty-item:hover .specialty-title { color: #C5A059; }
                .specialty-item:hover .specialty-arrow { opacity: 1; transform: translateX(4px); }
                .specialty-icon {
                    font-size: clamp(1rem, 2.5vw, 1.4rem);
                    color: rgba(197,160,89,0.4);
                    line-height: 1;
                    transition: all 0.35s ease;
                }
                .specialty-text {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 3px;
                }
                .specialty-title {
                    font-family: 'Outfit', sans-serif;
                    font-size: clamp(0.6rem, 1.3vw, 0.7rem);
                    color: rgba(248,249,250,0.75);
                    font-weight: 600;
                    letter-spacing: 1.5px;
                    text-transform: uppercase;
                    text-align: center;
                    transition: color 0.3s ease;
                }
                .specialty-desc {
                    font-family: 'Outfit', sans-serif;
                    font-size: clamp(0.5rem, 1.1vw, 0.58rem);
                    color: rgba(209,209,209,0.35);
                    letter-spacing: 0.5px;
                    text-align: center;
                    font-weight: 300;
                }
                .specialty-arrow {
                    font-size: 0.65rem;
                    color: #C5A059;
                    opacity: 0;
                    transition: all 0.3s ease;
                }
                /* Collapse to 2-col on mobile */
                @media (max-width: 575px) {
                    .specialties-strip {
                        grid-template-columns: repeat(2, 1fr);
                        max-width: 100%;
                    }
                    .specialty-item:nth-child(3) { border-left: none; }
                    .specialty-item { padding: 10px 6px; }
                }

                /* Decorative lines */
                .deco-line {
                    position: absolute;
                    left: 0; right: 0;
                    height: 1px;
                    pointer-events: none;
                }
                .deco-line-top {
                    top: clamp(70px, 15vh, 120px);
                    background: linear-gradient(90deg, transparent, rgba(197,160,89,0.2) 30%, rgba(197,160,89,0.2) 70%, transparent);
                }
                .deco-line-bottom {
                    bottom: clamp(55px, 10vh, 80px);
                    background: linear-gradient(90deg, transparent, rgba(197,160,89,0.15) 30%, rgba(197,160,89,0.15) 70%, transparent);
                }
                .vert-line {
                    position: absolute; top: 0; bottom: 0;
                    width: 1px; pointer-events: none;
                    background: linear-gradient(180deg, transparent, rgba(197,160,89,0.1) 30%, rgba(197,160,89,0.1) 70%, transparent);
                }
                .vert-line-left  { left: 8%; }
                .vert-line-right { right: 8%; }

                /* Corner brackets */
                .corner-bracket {
                    position: absolute;
                    width: 24px; height: 24px;
                    pointer-events: none;
                }

                /* Slide counter */
                .slide-counter {
                    position: absolute;
                    right: clamp(16px, 4%, 50px);
                    top: 50%;
                    transform: translateY(-50%);
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 10px;
                    z-index: 10;
                }
                .counter-current {
                    font-family: 'Outfit', sans-serif;
                    font-size: 0.85rem;
                    color: #C5A059;
                    font-weight: 700;
                    letter-spacing: 0;
                    line-height: 1;
                    min-width: 24px;
                    text-align: center;
                }
                .counter-line {
                    width: 1px; height: 44px;
                    background: linear-gradient(transparent, rgba(197,160,89,0.5), transparent);
                }
                .counter-total {
                    font-family: 'Outfit', sans-serif;
                    font-size: 0.75rem;
                    color: rgba(255,255,255,0.3);
                    font-weight: 400;
                    letter-spacing: 0;
                    line-height: 1;
                    min-width: 24px;
                    text-align: center;
                }

                /* Main content block */
                .hero-content {
                    position: relative;
                    z-index: 5;
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                    padding: 0 clamp(16px, 6%, 100px);
                }

                /* Index label */
                .hero-index-label {
                    display: inline-block;
                    font-family: 'Outfit', sans-serif;
                    font-size: clamp(0.5rem, 2vw, 0.72rem);
                    color: #C5A059;
                    letter-spacing: 6px;
                    font-weight: 600;
                    text-transform: uppercase;
                    margin-bottom: clamp(12px, 3vw, 24px);
                }

                /* Headings */
                .hero-heading-wrap {
                    margin-bottom: clamp(14px, 3vw, 24px);
                }
                .hero-h1 {
                    margin: 0;
                    font-family: 'Outfit', sans-serif;
                    font-size: clamp(2.2rem, 10vw, 7rem);
                    line-height: 1.05;
                    letter-spacing: clamp(-1px, -0.3vw, -2px);
                    text-transform: uppercase;
                }
                .hero-h1-light {
                    font-weight: 200;
                    color: #F8F9FA;
                }
                .hero-h1-gold {
                    font-weight: 600;
                    color: #C5A059;
                    position: relative;
                    display: inline-block;
                }
                .heading-underline {
                    position: absolute;
                    bottom: -4px; left: 0;
                    width: 100%; height: 2px;
                    background: linear-gradient(90deg, #C5A059, transparent);
                    display: block;
                }

                /* Sub-text */
                .hero-sub {
                    font-family: 'Outfit', sans-serif;
                    font-size: clamp(0.8rem, 2.2vw, 1rem);
                    color: rgba(209,209,209,0.7);
                    font-weight: 300;
                    letter-spacing: 0.3px;
                    line-height: 1.75;
                    max-width: min(520px, 90%);
                    text-align: center;
                    margin-bottom: clamp(22px, 5vw, 42px);
                }

                /* Ghost background word */
                .bg-ghost-word {
                    position: absolute;
                    bottom: 6%;
                    right: -2%;
                    font-family: 'Outfit', sans-serif;
                    font-size: clamp(60px, 16vw, 200px);
                    font-weight: 800;
                    color: transparent;
                    -webkit-text-stroke: 1px rgba(197,160,89,0.07);
                    text-transform: uppercase;
                    letter-spacing: -5px;
                    line-height: 1;
                    pointer-events: none;
                    user-select: none;
                    z-index: 1;
                    white-space: nowrap;
                }

                /* Dot indicators */
                .hero-indicators {
                    position: absolute;
                    bottom: clamp(16px, 4vh, 30px);
                    left: 50%;
                    transform: translateX(-50%);
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    z-index: 10;
                }
                .hero-dot {
                    height: 3px;
                    border-radius: 4px;
                    border: none;
                    cursor: pointer;
                    transition: all 0.4s cubic-bezier(0.19,1,0.22,1);
                    padding: 0;
                }

                /* Progress bar */
                .hero-progress-bar {
                    position: absolute;
                    bottom: 0; left: 0; right: 0;
                    height: 2px;
                    background: rgba(255,255,255,0.05);
                    z-index: 10;
                }
                .hero-progress-fill {
                    height: 100%;
                    background: linear-gradient(90deg, #C5A059, #B28F4E);
                    width: 0;
                }

                /* Scroll indicator */
                .scroll-indicator {
                    position: absolute;
                    bottom: 20px;
                    right: clamp(40px, 6%, 70px);
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 8px;
                    z-index: 10;
                }
                .scroll-label {
                    font-family: 'Outfit', sans-serif;
                    font-size: 0.5rem;
                    color: rgba(197,160,89,0.5);
                    letter-spacing: 4px;
                    font-weight: 600;
                    writing-mode: vertical-rl;
                    text-orientation: mixed;
                }
                .scroll-line {
                    width: 1px; height: 38px;
                    background: linear-gradient(transparent, rgba(197,160,89,0.4));
                    animation: scrollBounce 2s infinite ease-in-out;
                }

                /* Marquee */
                .marquee-wrap {
                    width: 100%;
                    overflow: hidden;
                    background: rgba(197,160,89,0.06);
                    border-top: 1px solid rgba(197,160,89,0.12);
                    border-bottom: 1px solid rgba(197,160,89,0.12);
                    padding: clamp(10px, 2vw, 14px) 0;
                    position: relative;
                    z-index: 5;
                }
                .marquee-track {
                    display: flex;
                    white-space: nowrap;
                    animation: marqueeScroll 20s linear infinite;
                }
                .marquee-text {
                    font-family: 'Outfit', sans-serif;
                    font-size: clamp(0.55rem, 1.5vw, 0.72rem);
                    color: rgba(197,160,89,0.7);
                    letter-spacing: 4px;
                    font-weight: 500;
                    text-transform: uppercase;
                }

                /* ── Extra small screens (< 400px) ── */
                @media (max-width: 399px) {
                    .hero-h1 { font-size: clamp(1.8rem, 12vw, 2.6rem); }
                    .hero-sub { font-size: 0.78rem; }
                    .btn-gold { padding: 10px 22px; font-size: 0.7rem; letter-spacing: 1.5px; }
                }

                /* ── Mobile ── */
                @media (max-width: 575px) {
                    .hero-content { padding: 0 20px; }
                    .hero-index-label { letter-spacing: 4px; }
                    .deco-line-top { top: 60px; }
                }

                /* ── Tablet ── */
                @media (min-width: 576px) and (max-width: 991px) {
                    .hero-content { padding: 0 40px; }
                    .bg-ghost-word { font-size: clamp(60px, 14vw, 130px); }
                }

                /* ── Desktop ── */
                @media (min-width: 992px) {
                    .hero-content { padding: 0 clamp(60px, 8%, 120px); }
                }

                /* ═══ Keyframes ═══ */
                @keyframes heroFadeUp {
                    from { opacity: 0; transform: translateY(28px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes heroFadeDown {
                    from { opacity: 1; transform: translateY(0); }
                    to   { opacity: 0; transform: translateY(-18px); }
                }
                @keyframes heroSlideUp {
                    from { opacity: 0; transform: translateY(50px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes bgWordIn {
                    from { opacity: 0; transform: translateX(60px) skewX(-4deg); }
                    to   { opacity: 1; transform: translateX(0) skewX(0); }
                }
                @keyframes bgWordOut {
                    from { opacity: 1; transform: translateX(0) skewX(0); }
                    to   { opacity: 0; transform: translateX(-60px) skewX(4deg); }
                }
                @keyframes heroProgress {
                    from { width: 0%; }
                    to   { width: 100%; }
                }
                @keyframes marqueeScroll {
                    from { transform: translateX(0); }
                    to   { transform: translateX(-50%); }
                }
                @keyframes scrollBounce {
                    0%, 100% { opacity: 1; transform: scaleY(1); }
                    50%      { opacity: 0.4; transform: scaleY(0.5); }
                }
                @keyframes sidebarFadeIn {
                    from { opacity: 0; }
                    to   { opacity: 1; }
                }
            `}</style>
        </main>
    );
};

export default Home;
